import type { TFunction } from 'i18next';
import type { CompanyLicenses } from '../../api/apiService';
import { track } from '../../lib/analytics';
import { AnalyticsEvent } from '../../lib/analyticsEvents';

export interface PurchaseParams {
  purchase_success?: string;
  purchase_error?: string;
  licenses?: string;
  product_id?: string;
  is_new?: string;
}

export interface PurchaseHandlersDeps {
  params: PurchaseParams;
  isOwnerProfile: boolean;
  t: TFunction<'translation'>;
  setPurchaseData: (data: { licenses: number; productId: string; isNew: boolean } | null) => void;
  setPurchaseSuccessModal: (visible: boolean) => void;
  setAlertMessage: (message: string) => void;
  setAlertModalVisible: (visible: boolean) => void;
  fetchCompanyLicenses: () => Promise<{ ok: boolean; data?: CompanyLicenses }>;
  updateLicenseInfo: (info: CompanyLicenses) => void;
  clearPurchaseParams?: () => void;
}

export const handlePurchaseParams = async ({
  params,
  isOwnerProfile,
  t,
  setPurchaseData,
  setPurchaseSuccessModal,
  setAlertMessage,
  setAlertModalVisible,
  fetchCompanyLicenses,
  updateLicenseInfo,
  clearPurchaseParams,
}: PurchaseHandlersDeps) => {
  const { purchase_success, purchase_error, licenses, product_id, is_new } = params;

  if (purchase_success === 'true') {
    const licenseCount = parseInt(licenses || '0', 10);
    const productId = product_id || '';
    const isNew = is_new === 'true';

    setPurchaseData({ licenses: licenseCount, productId, isNew });
    setPurchaseSuccessModal(true);

    // purchase_completed is emitted only by the Stripe webhook. The checkout
    // return is UI confirmation and has no authoritative transaction id.
    if (isOwnerProfile) {
      try {
        const response = await fetchCompanyLicenses();
        if (response.ok && response.data) {
          updateLicenseInfo(response.data);
        }
      } catch {
        // Ignore errors refreshing licenses in the success flow
      }
    }

    clearPurchaseParams?.();
    return;
  }

  if (purchase_error) {
    track(AnalyticsEvent.PaymentFailed, {
      reason: 'checkout_return_error',
      detail: purchase_error,
      user_role: isOwnerProfile ? 'owner' : 'driver',
    });
    setAlertMessage(
      t('purchase.error', {
        defaultValue: 'There was an error processing your purchase. Please contact support.',
      })
    );
    setAlertModalVisible(true);
    clearPurchaseParams?.();
  }
};

